import React, { useEffect, useState } from 'react';
import { GameConfig } from '../models/GameRegistry';
import { AchievementDefinition, Achievement, gameAPI } from '../services/GameAPI';
import '../styles/GameLaunchModal.css';
import songlyWizard from '../../../shared/assets/images/characters/Songly Wizard.png';

interface GameLaunchModalProps {
    game: GameConfig;
    onPlay: () => void;
    onClose: () => void;
    achievements: AchievementDefinition[];
    userAchievements: Achievement[];
}

const GameLaunchModal: React.FC<GameLaunchModalProps> = ({
    game,
    onPlay,
    onClose,
    achievements,
    userAchievements
}) => {
    const [timeToReset, setTimeToReset] = useState<string>('');
    const [potentialCash, setPotentialCash] = useState<number>(0);
    const [earnedTodayCash, setEarnedTodayCash] = useState<number>(0);
    const [resetUrgency, setResetUrgency] = useState<'green' | 'orange' | 'red'>('green');
    const [hoveredTrophy, setHoveredTrophy] = useState<AchievementDefinition | null>(null);
    const [trophyPage, setTrophyPage] = useState<number>(0);

    // Helper: determine if an achievement should behave as a daily goal
    const isDailyAchievement = (a: AchievementDefinition) =>
        a.frequency === 'daily' || a.type === 'daily';

    // Filter for daily achievements (Today's Goals)
    const dailyAchievements = achievements.filter(isDailyAchievement);
    // Filter for standard achievements (Trophy Case)
    const standardAchievements = achievements.filter(a => !isDailyAchievement(a));

    const isAchievementUnlocked = (id: string, frequency?: string) => {
        const userAchievement = userAchievements.find(ua => ua.achievementId === id && ua.gameId === game.id);

        if (!userAchievement) return false;

        if (frequency === 'daily') {
            const unlockedDate = new Date(userAchievement.unlockedAt);
            const today = new Date();
            return (
                unlockedDate.getDate() === today.getDate() &&
                unlockedDate.getMonth() === today.getMonth() &&
                unlockedDate.getFullYear() === today.getFullYear()
            );
        }

        return true;
    };

    useEffect(() => {
        // Calculate potential cash for today from all daily achievements
        const dailyPotential = dailyAchievements.reduce((sum, a) => {
            return sum + a.museCashReward;
        }, 0);
        setPotentialCash(dailyPotential);

        // Calculate how much Muse Cash has been earned today from daily goals
        let earnedToday = 0;
        dailyAchievements.forEach((a) => {
            if (isAchievementUnlocked(a.id, 'daily')) {
                earnedToday += a.museCashReward;
            }
        });
        setEarnedTodayCash(earnedToday);

        // Update countdown timer and urgency color
        const updateTimer = () => {
            const now = new Date();
            const tomorrow = new Date(now);
            tomorrow.setHours(24, 0, 0, 0);
            const diff = tomorrow.getTime() - now.getTime();

            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const totalMinutes = Math.floor(diff / (1000 * 60));

            setTimeToReset(`${hours}h ${minutes}m`);

            if (totalMinutes <= 60) {
                setResetUrgency('red');
            } else if (totalMinutes <= 180) {
                setResetUrgency('orange');
            } else {
                setResetUrgency('green');
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 60000); // Update every minute

        return () => clearInterval(interval);
    }, [dailyAchievements, userAchievements, game.id]);

    const totalDailyGoals = dailyAchievements.length;
    const completedDailyGoals = dailyAchievements.filter(a => isAchievementUnlocked(a.id, 'daily')).length;
    const remainingCashToday = Math.max(potentialCash - earnedTodayCash, 0);
    const progressPct = potentialCash > 0 ? Math.min((earnedTodayCash / potentialCash) * 100, 100) : 0;

    const unlockedStandardAchievements = standardAchievements.filter(a => isAchievementUnlocked(a.id));
    const lockedStandardAchievements = standardAchievements.filter(a => !isAchievementUnlocked(a.id));
    const nextAchievement = lockedStandardAchievements.length > 0
        ? lockedStandardAchievements.reduce((best, current) => {
            if (!best) return current;
            return current.museCashReward < best.museCashReward ? current : best;
        }, lockedStandardAchievements[0])
        : null;

    const TROPHIES_PER_PAGE = 4;
    const totalTrophyPages = Math.max(1, Math.ceil(standardAchievements.length / TROPHIES_PER_PAGE));
    const trophyPageClamped = Math.min(trophyPage, totalTrophyPages - 1);
    const visibleTrophies = standardAchievements.slice(
        trophyPageClamped * TROPHIES_PER_PAGE,
        trophyPageClamped * TROPHIES_PER_PAGE + TROPHIES_PER_PAGE
    );

    return (
        <div className="game-launch-modal-overlay" onClick={onClose}>
            <div className="game-launch-modal" onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>×</button>

                <div className="modal-header">
                    <img
                        src={songlyWizard}
                        alt="Songly Wizard"
                        className="modal-wizard-icon"
                    />
                    <div className="modal-header-right">
                        <img
                            src={game.thumbnail}
                            alt={game.title}
                            className="modal-game-icon"
                        />
                        <h2 className="modal-game-title">{game.title}</h2>
                    </div>
                </div>

                <div className="modal-body">
                    <div className="modal-main-grid">
                        <div className="daily-goals-section">
                            <div className="section-title">
                                <span>🎯 Today's Goals</span>
                                {totalDailyGoals > 0 && (
                                    <span className="section-pill">
                                        {completedDailyGoals}/{totalDailyGoals}
                                    </span>
                                )}
                            </div>

                            <div className="daily-goals-grid">
                                {dailyAchievements.map(achievement => {
                                    const isUnlocked = isAchievementUnlocked(achievement.id, 'daily');

                                    return (
                                        <div
                                            key={achievement.id}
                                            className={`daily-goal-item ${isUnlocked ? 'completed' : 'incomplete'}`}
                                        >
                                            <div className="goal-icon">{achievement.iconUrl}</div>
                                            <div className="goal-info">
                                                <div className="goal-title">{achievement.title}</div>
                                                <div className="goal-desc">{achievement.description}</div>
                                            </div>
                                            <div className="goal-reward">
                                                <span className="reward-amount">+{achievement.museCashReward}</span>
                                                <span>💰</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="trophy-case-section">
                            <div className="section-title">
                                <span>🏆 Trophy Case</span>
                                <span className="section-pill section-pill--muted">
                                    {unlockedStandardAchievements.length}/{standardAchievements.length}
                                </span>
                            </div>

                            <div className="trophy-carousel-row">
                                {totalTrophyPages > 1 && trophyPageClamped > 0 && (
                                    <button
                                        type="button"
                                        className="trophy-carousel-arrow trophy-carousel-arrow-prev"
                                        onClick={() =>
                                            setTrophyPage(prev =>
                                                prev <= 0 ? 0 : prev - 1
                                            )
                                        }
                                        aria-label="Previous trophies"
                                    >
                                        ◀
                                    </button>
                                )}

                                <div className="trophy-grid">
                                    {visibleTrophies.map(achievement => {
                                        const isUnlocked = isAchievementUnlocked(achievement.id);
                                        return (
                                            <div
                                                key={achievement.id}
                                                className={`trophy-item ${isUnlocked ? 'unlocked' : 'locked'}`}
                                                onMouseEnter={() => setHoveredTrophy(achievement)}
                                                onMouseLeave={() =>
                                                    setHoveredTrophy(current =>
                                                        current?.id === achievement.id ? null : current
                                                    )
                                                }
                                                onClick={() =>
                                                    setHoveredTrophy(current =>
                                                        current?.id === achievement.id ? null : achievement
                                                    )
                                                }
                                            >
                                                {isUnlocked && (
                                                    <div className="trophy-icon">
                                                        {achievement.iconUrl || '🏆'}
                                                    </div>
                                                )}
                                                {!isUnlocked && (
                                                    <div className="trophy-flip">
                                                        <div className="trophy-face trophy-face-front">
                                                            ?
                                                        </div>
                                                        <div className="trophy-face trophy-face-back">
                                                            +{achievement.museCashReward}💰
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {totalTrophyPages > 1 && trophyPageClamped < totalTrophyPages - 1 && (
                                    <button
                                        type="button"
                                        className="trophy-carousel-arrow trophy-carousel-arrow-next"
                                        onClick={() =>
                                            setTrophyPage(prev =>
                                                prev >= totalTrophyPages - 1 ? totalTrophyPages - 1 : prev + 1
                                            )
                                        }
                                        aria-label="Next trophies"
                                    >
                                        ▶
                                    </button>
                                )}
                            </div>

                            {nextAchievement && (
                                <div className="next-achievement">
                                    <div className="next-achievement-title">
                                        Next Goal: {nextAchievement.title}
                                    </div>
                                    <div className="next-achievement-body">
                                        <div className="next-achievement-icon">
                                            {nextAchievement.iconUrl || '🏆'}
                                        </div>
                                        <div className="next-achievement-info">
                                            <div className="next-achievement-reward">
                                                +{nextAchievement.museCashReward}💰
                                            </div>
                                            <div className="next-achievement-description">
                                                {nextAchievement.description}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {hoveredTrophy && (
                    <div className="trophy-tooltip-overlay">
                        <div className="trophy-tooltip-card">
                            <div className="trophy-tooltip-header">
                                <div className="trophy-tooltip-icon">
                                    {hoveredTrophy.iconUrl || '🏆'}
                                </div>
                                <div className="trophy-tooltip-main">
                                    <div className="trophy-tooltip-title">
                                        {hoveredTrophy.title}
                                    </div>
                                    <div className="trophy-tooltip-subtitle">
                                        +{hoveredTrophy.museCashReward}💰 reward
                                    </div>
                                </div>
                            </div>
                            {hoveredTrophy.description && (
                                <div className="trophy-tooltip-body">
                                    {hoveredTrophy.description}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="play-section">
                    <div className="total-potential">
                        <span>Earn up to +{potentialCash}💰 today!</span>
                    </div>

                    <button className="play-button" onClick={onPlay}>
                        <span className="play-button-icon">▶</span>
                        <span className="play-button-label">PLAY NOW</span>
                    </button>

                    <div className={`daily-reset-timer daily-reset-timer--${resetUrgency}`}>
                        ⏰ Fresh goals in {timeToReset}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GameLaunchModal;
